import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { createApiUrlWithPrefix, createApiUrl } from '../config/api';
import { Check, X, Eye, Trash2, RefreshCw, FileText, Camera } from 'lucide-react';
import UserCreationModal from './UserCreationModal';
import { useUserCounts } from '../contexts/UserCountsContext';

function Subscribers() {
  const { refreshUserCounts } = useUserCounts();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('0'); // Default to '0' = Pending only
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== '') {
        params.append('status', statusFilter);
      }
      params.append('page', page);
      params.append('pageSize', pageSize);

      const queryString = params.toString();
      const url = createApiUrlWithPrefix(`subscribers${queryString ? `?${queryString}` : ''}`);
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.items) {
        setSubscribers(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
      } else {
        // Fallback for backward compatibility or error
        setSubscribers(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast.error(error.response?.data?.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleApprove = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowApproveModal(true);
  };

  const handleUserCreated = async (newUser) => {
    // After user is created via the form, mark the subscriber as converted
    if (selectedSubscriber && newUser && newUser.id) {
      try {
        // Mark subscriber as converted with the newly created user ID
        await axios.put(
          createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/convert`),
          newUser.id, // Send userId as body
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        toast.success('Subscriber approved and user created successfully!');
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh the subscribers list to remove the approved subscriber
        // Ensure we're still filtering by pending status (0)
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts(); // Refresh the subscriber count in navigation
      } catch (error) {
        console.error('Failed to mark subscriber as converted:', error);
        // Even if marking as converted fails, the user was created successfully
        toast.success('User created successfully! Note: Subscriber status may need manual update.');
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh even on error
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts();
      }
    }
  };

  const handleReject = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to reject this subscription request?')) {
      return;
    }

    try {
      await axios.post(
        createApiUrlWithPrefix(`subscribers/${subscriberId}/reject`),
        null,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success('Subscriber rejected successfully');
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to reject subscriber:', error);
      toast.error(error.response?.data?.message || 'Failed to reject subscriber');
    }
  };

  const handleDelete = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to delete this subscription request?')) {
      return;
    }

    try {
      await axios.delete(createApiUrlWithPrefix(`subscribers/${subscriberId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Subscriber deleted successfully');
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to delete subscriber:', error);
      toast.error(error.response?.data?.message || 'Failed to delete subscriber');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      1: { label: 'Approved', color: 'bg-green-100 text-green-800' },
      2: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      3: { label: 'Converted', color: 'bg-blue-100 text-blue-800' }
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const memoizedInitialData = React.useMemo(() => {
    if (!selectedSubscriber) return null;
    return {
      id: selectedSubscriber.id,
      firstName: selectedSubscriber.firstName,
      lastName: selectedSubscriber.lastName,
      email: selectedSubscriber.email,
      phoneNumber: selectedSubscriber.phoneNumber,
      birthDate: selectedSubscriber.birthDate ? selectedSubscriber.birthDate.split('T')[0] : '',
      address: selectedSubscriber.address,
      bloodType: selectedSubscriber.bloodType,
      photoPath: selectedSubscriber.photoPath,
      pjDocumentPath: selectedSubscriber.pjDocumentPath,
    };
  }, [selectedSubscriber]);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Subscribers</h2>
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="0">Pending</option>
            <option value="">All Statuses</option>
            <option value="1">Approved</option>
            <option value="2">Rejected</option>
            <option value="3">Converted</option>
          </select>
          <button
            onClick={fetchSubscribers}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscribers...</p>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg">
          <p className="text-gray-600">No subscribers found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-full max-h-[calc(100vh-12rem)]">
          <div className="overflow-x-auto overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Birth Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscriber.firstName} {subscriber.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{subscriber.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{subscriber.phoneNumber || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(subscriber.birthDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(subscriber.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(subscriber.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubscriber(subscriber);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {subscriber.status === 0 && (
                          <>
                            <button
                              onClick={() => handleApprove(subscriber)}
                              className="text-green-600 hover:text-green-900"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(subscriber.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(subscriber.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === i + 1
                          ? 'z-10 bg-green-50 border-green-500 text-green-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedSubscriber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Subscriber Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedSubscriber(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">First Name</label>
                  <p className="text-gray-900">{selectedSubscriber.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Name</label>
                  <p className="text-gray-900">{selectedSubscriber.lastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedSubscriber.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  <p className="text-gray-900">{selectedSubscriber.phoneNumber || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Birth Date</label>
                  <p className="text-gray-900">{formatDate(selectedSubscriber.birthDate)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Blood Type</label>
                  <p className="text-gray-900">{selectedSubscriber.bloodType || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="text-gray-900">{selectedSubscriber.address || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedSubscriber.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Created At</label>
                  <p className="text-gray-900">{formatDate(selectedSubscriber.createdAt)}</p>
                </div>
                {selectedSubscriber.notes && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-700">Notes</label>
                    <p className="text-gray-900">{selectedSubscriber.notes}</p>
                  </div>
                )}

                {/* Uploaded Documents */}
                <div className="col-span-2 mt-4">
                  <label className="text-sm font-medium text-gray-700 block mb-3">Uploaded Documents</label>
                  <div className="flex gap-8">
                    {/* Photo */}
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Profile Photo</p>
                      {selectedSubscriber.photoPath ? (
                        <a href={createApiUrl(selectedSubscriber.photoPath)} target="_blank" rel="noopener noreferrer" className="block relative group">
                          <img
                            src={createApiUrl(selectedSubscriber.photoPath)}
                            alt="Profile"
                            className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200 shadow-sm group-hover:border-primary-500 transition-colors"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all">
                            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                          </div>
                        </a>
                      ) : (
                        <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                          <Camera className="w-8 h-8 mb-2 opacity-50" />
                          <span className="text-xs">No Photo</span>
                        </div>
                      )}
                    </div>

                    {/* PJ Document */}
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Inscription Doc</p>
                      {selectedSubscriber.pjDocumentPath ? (
                        <a
                          href={createApiUrl(selectedSubscriber.pjDocumentPath)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-32 h-32 bg-blue-50 rounded-xl flex flex-col items-center justify-center border-2 border-blue-100 text-blue-600 hover:bg-blue-100 transition-colors group"
                        >
                          <FileText className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" />
                          <span className="text-xs font-medium">View Document</span>
                        </a>
                      ) : (
                        <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                          <FileText className="w-8 h-8 mb-2 opacity-50" />
                          <span className="text-xs">No Document</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedSubscriber(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal - Use same form as Riders page */}
      {showApproveModal && selectedSubscriber && (
        <UserCreationModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setSelectedSubscriber(null);
          }}
          role={null}
          mode="approve"
          onSubmit={async (approveData) => {
            // Use the approve endpoint
            try {
              const response = await axios.post(
                createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/approve`),
                approveData,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              const newUser = response.data.User || response.data.user;

              toast.success('Subscriber approved and user created successfully!');
              setShowApproveModal(false);
              setSelectedSubscriber(null);
              setStatusFilter('0');
              await fetchSubscribers();
              refreshUserCounts();
            } catch (error) {
              console.error('Failed to approve subscriber:', error);
              toast.error(error.response?.data?.message || 'Failed to approve subscriber');
              throw error;
            }
          }}
          onUserCreated={handleUserCreated}
          initialData={memoizedInitialData}
        />
      )}
    </div>
  );
}

export default Subscribers;

